import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useUploadDrawer } from '@/user_files/drive/hooks/useUploadDrawer'
import { NewFolderPlus, Upload } from '@/assets/icons'
import { ActionBar } from '@/components/action-bar/ActionBar'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { selectCurrentFolderId, selectAreCurrentFolderChildrenLoaded, selectCurrentFolderDriveVisibleChildren } from '@/user_files/folders/store/selectors'
import { FolderBreadcrumbs } from '@/user_files/folders/components/FolderBreadcrumbs'
import { FolderList } from '@/user_files/folders/components/FolderList'
import { CreateFolderDialog } from '@/user_files/folders/components/CreateFolderDialog'
import { RenameItemDialog } from '@/user_files/drive/components/RenameItemDialog'
import { DriveDropzone } from '@/user_files/drive/components/DriveDropzone'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { selectCurrentFolderDriveVisibleFiles, selectFileListRequest } from '@/user_files/files/store/selectors'
import { useDriveSelection } from '@/user_files/drive/hooks/useDriveSelection'
import { useDeleteDialog } from '@/user_files/drive/hooks/useDeleteDialog'
import { useMoveDialog } from '@/user_files/drive/hooks/useMoveDialog'
import { useMoveConflict } from '@/user_files/drive/hooks/useMoveConflict'
import { useRenameDialog } from '@/user_files/drive/hooks/useRenameDialog'
import { useDriveUpload } from '@/user_files/drive/hooks/useDriveUpload'
import { BulkActionBar } from '@/user_files/files/components/BulkActionBar'
import { DeleteItemsDialog } from '@/user_files/drive/components/DeleteItemsDialog'
import { UserFilesOperationOverlay } from '@/user_files/batching/components/UserFilesOperationOverlay'
import { MoveItemsDialog } from '@/user_files/move/components/MoveItemsDialog'
import { DynamicContentViewer } from '@/user_files/drive/components/DynamicContentViewer'
import SelectActiveMatterPanel from '@/matter/components/SelectActiveMatterPanel'
import { FileUploadDrawer } from '@/user_files/drive/components/file-upload-drawer/FileUploadDrawer'
import { ConflictModal } from '@/user_files/drive/components/ConflictModal'
import { useProcessingSummary } from '@/uppy-file-upload/hooks/useProcessingSummary'
import { selectIsUploading } from '@/uppy-file-upload/store/selectors'
import { BottomStickyBar } from '@/user_files/drive/components/BottomStickyBar'
import { selectActiveMatter } from '@/matter/store/selectors'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { UnassignedMatterBanner } from '@/user_files/drive/components/UnassignedMatterBanner'
import { useUserFile } from '@/user_files/files/hooks/useUserFiles'
import { useViewUserFile } from '@/user_files/files/hooks/useViewUserFile'
import ErrorCard from '@/components/error/ErrorCard'
import { matterPaths } from '@/routing/paths'

/**
 * DriveView
 *
 * Main orchestrator for the user-files drive surface.
 *
 * This component intentionally keeps the top-level feature flow visible in one
 * place: folder/file fetch triggers, upload auto-start wiring, selection hooks,
 * overlay/dialog composition, and final layout wiring.
 *
 * Presentational states can be split into leaf components (e.g. matter-required
 * empty state), while cross-feature orchestration stays here to avoid hidden
 * control flow and hook indirection.
 *
 * Note: Requires a matter to be selected. Files are organized per-matter.
 */
export function DriveView() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  // Redux state
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const currentFolderId = useAppSelector(selectCurrentFolderId)
  const fileListRequest = useAppSelector((state) => selectFileListRequest(state, { folderId: currentFolderId }))
  const childrenLoaded = useAppSelector(selectAreCurrentFolderChildrenLoaded)
  const files = useAppSelector(selectCurrentFolderDriveVisibleFiles)
  const folders = useAppSelector(selectCurrentFolderDriveVisibleChildren)
  const isUploading = useAppSelector(selectIsUploading)
  const activeMatter = useAppSelector(selectActiveMatter)
  const viewUserFileId = searchParams.get('viewUserFile')
  const { file: fileToView, loading: fileToViewLoading, error: fileToViewError } = useUserFile(viewUserFileId)
  const viewUserFile = useViewUserFile()

  // Matter-centric: folders only exist within a matter context
  const hasMatterSelected = !!activeMatterId
  const isUnassigned = isUnassignedMatter(activeMatter)

  const {
    selectedSelectableFileIds,
    selectedFolderIds,
    clearSelection,
    isFileSelected,
    toggleFileSelect,
    isFolderSelected,
    toggleFolderSelect,
    selectedCount,
    isAllSelected,
    selectAll,
  } = useDriveSelection(files, folders, currentFolderId)

  const { deleteDialog, openDeleteForSelected, openDeleteForFile, openDeleteForFolder, closeDeleteDialog, confirmDelete, isDeleting, deleteError } =
    useDeleteDialog({ files, folders, currentFolderId, selectedSelectableFileIds, selectedFolderIds, clearSelection })

  const { moveDialog, openMoveForSelected, openMoveItems, closeMoveDialog, isSubmitting } = useMoveDialog({
    files,
    folders,
    currentFolderId,
    selectedSelectableFileIds,
    selectedFolderIds,
  })

  const { checkAndConfirmMove, moveConflictDialog } = useMoveConflict({
    onComplete: clearSelection,
  })

  const { renameDialog, openRenameForFile, openRenameForFolder, closeRenameDialog, confirmRename, isRenaming, renameError, occupiedNames } = useRenameDialog()

  const {
    openUploadPicker,
    disabled: dropzoneDisabled,
    fileInputRef,
    addFiles,
    onFilesPicked,
    cancelUpload,
    removeFile,
    hasQueuedFiles,
    conflictDialog,
  } = useDriveUpload({ folderId: currentFolderId })

  const { totalCount: processingTotalCount } = useProcessingSummary()
  const hasActiveUploads = processingTotalCount > 0 || hasQueuedFiles || isUploading

  const { visible, expanded, sidebarHidden, onFilesDrag, onExpandedChange, onClose } = useUploadDrawer({
    processingTotalCount,
    hasActiveUploads,
  })

  // Fetch children when navigating to a folder that hasn't loaded children yet
  useEffect(() => {
    if (currentFolderId && !childrenLoaded) {
      dispatch(UserFoldersActions.fetchFolderChildren({ folderId: currentFolderId }))
    }
  }, [currentFolderId, childrenLoaded, dispatch])

  // Fetch files when navigating to a folder
  useEffect(() => {
    if (currentFolderId) {
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId: currentFolderId }))
    }
  }, [currentFolderId, dispatch])

  useEffect(() => {
    if (!viewUserFileId) return
    if (fileToViewLoading) return
    if (!fileToView && !fileToViewError) return

    if (fileToView && !fileToViewError) {
      viewUserFile(fileToView)
    }

    const cleanedParams = new URLSearchParams(searchParams)
    cleanedParams.delete('viewUserFile')
    setSearchParams(cleanedParams, { replace: true })
  }, [viewUserFileId, fileToViewLoading, fileToViewError, fileToView, searchParams, setSearchParams, viewUserFile])

  const handleNewFolder = () => {
    dispatch(UserFoldersActions.openDialog({ dialogType: 'create', folderId: null }))
  }

  // No matter selected - show prompt to select one
  if (!hasMatterSelected) {
    return (
      <div className="w-full h-full pt-[20vh]">
        <SelectActiveMatterPanel variant="dashboard" title="What are you working on today?" description="Select a Matter to access your files." />
      </div>
    )
  }

  if (fileListRequest.status === 'error' && matterId) {
    const isMissingOrDenied = fileListRequest.statusCode === 403 || fileListRequest.statusCode === 404
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-6">
        {isMissingOrDenied ? (
          <ErrorCard
            code="404"
            title="Folder not available"
            message="This folder may have been deleted or is no longer accessible to your account."
            actionLabel="Back to Matter Files"
            onClick={() => navigate(matterPaths.userFiles.root(matterId))}
          />
        ) : (
          <ErrorCard
            code="500"
            title="Something went wrong"
            message="We hit an unexpected error loading this folder. Please try again."
            actionLabel="Retry"
            onClick={() => dispatch(UserFilesActions.fetchFilesForFolder({ folderId: currentFolderId! }))}
          />
        )}
      </div>
    )
  }

  return (
    <>
      <UserFilesOperationOverlay />
      <DynamicContentViewer>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col pt-2 md:pt-3 lg:pt-7">
          <div className="flex w-full min-w-0 flex-1 flex-col min-h-0 px-0 md:px-4 lg:px-10">
            {isUnassigned && (
              <div className="mb-5">
                <UnassignedMatterBanner />
              </div>
            )}

            {/* Header with breadcrumbs + New Folder action */}
            {/* -ml-2 offsets the breadcrumb items' internal px-2 padding so text aligns flush with the container edge */}
            <ActionBar
              content={
                <div className="-ml-2">
                  <FolderBreadcrumbs />
                </div>
              }
              actions={[
                {
                  icon: <Upload className="h-4 w-4" />,
                  label: 'Upload',
                  onClick: openUploadPicker,
                  disabled: isUnassigned,
                },
                {
                  icon: <NewFolderPlus className="h-4 w-4" />,
                  label: 'New Folder',
                  onClick: handleNewFolder,
                  disabled: isUnassigned,
                },
              ]}
            />

            <DriveDropzone
              disabled={dropzoneDisabled || isUnassigned}
              fileInputRef={fileInputRef}
              addFiles={addFiles}
              onFilesPicked={onFilesPicked}
              onFilesBeingDragged={onFilesDrag}
            >
              {/* Bulk action bar */}
              {/* -ml-2 offsets the plain button's internal pl-2 so "X items selected" text aligns flush with the container edge */}
              <div className="-ml-2">
                <BulkActionBar selectedCount={selectedCount} onMove={openMoveForSelected} onDelete={openDeleteForSelected} onClearSelection={clearSelection} />
              </div>

              {/* Folder and file list */}
              <FolderList
                selection={{
                  isFileSelected,
                  toggleFileSelect,
                  openRenameForFile: (fileId) => {
                    const file = files.find((f) => f.id === fileId)
                    if (file) openRenameForFile(file)
                  },
                  openDeleteForFile,
                  openMoveItems,
                  isFolderSelected,
                  toggleFolderSelect,
                  openRenameForFolder: (folderId) => {
                    const folder = folders.find((f) => f.id === folderId)
                    if (folder) openRenameForFolder(folder)
                  },
                  openDeleteForFolder,
                  isAllSelected,
                  selectAll,
                }}
              />

              {/*
                Stays a React child of the dropzone (so drag events still bubble to it), but renders
                into document.body so it escapes the content viewer's `inert` subtree — on tablet/mobile
                the viewer is a Headless UI Dialog that inerts the whole app tree while open, which would
                otherwise make the fixed drawer non-interactive (can't expand/collapse or remove rows).
              */}
              {visible &&
                createPortal(
                  <FileUploadDrawer
                    expanded={expanded}
                    onExpandedChange={onExpandedChange}
                    onClose={onClose}
                    onCancel={cancelUpload}
                    onDismiss={removeFile}
                    offsetForSidebar={!sidebarHidden}
                  />,
                  document.body
                )}
            </DriveDropzone>
          </div>
        </div>
      </DynamicContentViewer>

      {/* Bottom sticky bar */}
      <BottomStickyBar />
      {/* Dialogs (conditionally rendered based on dialog state) */}
      <CreateFolderDialog />
      <RenameItemDialog
        open={renameDialog.open}
        item={renameDialog.item}
        isRenaming={isRenaming}
        error={renameError}
        occupiedNames={occupiedNames}
        onClose={closeRenameDialog}
        onConfirm={confirmRename}
      />
      <DeleteItemsDialog
        open={deleteDialog.open}
        files={deleteDialog.files}
        folders={deleteDialog.folders}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        error={deleteError}
      />
      <ConflictModal {...conflictDialog} operation="upload" />
      {moveDialog.open && activeMatterId && currentFolderId && (
        <MoveItemsDialog
          open
          initialMatterId={activeMatterId!}
          initialFolderId={currentFolderId!}
          fileIds={moveDialog.fileIds}
          folderIds={moveDialog.folderIds}
          submitting={isSubmitting}
          error={null}
          onClose={closeMoveDialog}
          onConfirm={(destinationFolderId) => {
            checkAndConfirmMove({
              fileIds: moveDialog.fileIds,
              folderIds: moveDialog.folderIds,
              sourceFolderId: currentFolderId!,
              destinationFolderId,
              filePreconditions: moveDialog.filePreconditions,
              folderPreconditions: moveDialog.folderPreconditions,
            })
            closeMoveDialog()
          }}
        />
      )}
      <ConflictModal {...moveConflictDialog} operation="move" />
    </>
  )
}
